// WAAP AI-WAF → Web Access Control.
//
// Two surfaces, backed by the PRIVATE portal via eagle_waf/website/siteadvanced:
//   - Website Whitelist: per-site URL paths that bypass AI-WAF detection, stored
//     in the site config under `website_whitelist` (read-modify-write on /site).
//     The website list itself reuses the page-defacement sites list (GET /sites).
//   - Remove Auto-Web ACL: drop an IP (or all via 0.0.0.0) from the auto Web ACL.
//
//   GET  /waap/domains/:id/website-whitelist/:siteId  → portal GET /site/:siteId
//   PUT  /waap/domains/:id/website-whitelist/:siteId  → portal PUT /site (patch website_whitelist)
//   POST /waap/domains/:id/auto-black-list            → portal POST /auto_black_list {ip}

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** One Website-Whitelist entry. An empty ruleId bypasses all rules for the path. */
export interface WhitelistRule {
  path: string;
  /** Comma-separated rule ids; empty = bypass all rules. */
  ruleId: string;
  remark: string;
}

function whitelistPath(domainId: string, siteId: string | number): string {
  return `/waap/domains/${domainId}/website-whitelist/${siteId}`;
}

export const webAccessKeys = {
  whitelist: (domainId: string, siteId: string | number) =>
    ['waap', 'web-access', domainId, 'whitelist', String(siteId)] as const,
};

/** Fetch a site's Website-Whitelist rules. */
export function useWebsiteWhitelist(domainId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: webAccessKeys.whitelist(domainId ?? '', siteId ?? ''),
    enabled: !!domainId && !!siteId,
    retry: false,
    queryFn: async (): Promise<WhitelistRule[]> => {
      const res = await serviceApiV3.get<WhitelistRule[] | { data: WhitelistRule[] }>(
        whitelistPath(domainId as string, siteId as string),
      );
      const body = res.data as WhitelistRule[] | { data?: WhitelistRule[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Save a site's Website-Whitelist rules. */
export function useUpdateWebsiteWhitelist(
  domainId: string,
  siteId: number | string,
  options?: UseMutationOptions<unknown, unknown, WhitelistRule[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rules: WhitelistRule[]) => {
      const res = await serviceApiV3.put(whitelistPath(domainId, siteId), rules);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: webAccessKeys.whitelist(domainId, siteId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Remove an IP (or all, via 0.0.0.0) from the auto Web ACL. */
export function useRemoveAutoBlackList(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  return useMutation({
    mutationFn: async (ip: string) => {
      const res = await serviceApiV3.post(
        `/waap/domains/${domainId}/auto-black-list`,
        { ip },
      );
      return res.data;
    },
    ...options,
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------

function serviceWhitelistPath(serviceId: string, siteId: string | number): string {
  return `/services/${serviceId}/waap/website-whitelist/${siteId}`;
}

export const webAccessServiceKeys = {
  whitelist: (serviceId: string, siteId: string | number) =>
    ['waap', 'web-access', 'service', serviceId, 'whitelist', String(siteId)] as const,
};

/** Fetch a site's Website-Whitelist rules, service-scoped. */
export function useServiceWebsiteWhitelist(
  serviceId: string | undefined,
  siteId: string | undefined,
) {
  return useQuery({
    queryKey: webAccessServiceKeys.whitelist(serviceId ?? '', siteId ?? ''),
    enabled: !!serviceId && !!siteId,
    retry: false,
    queryFn: async (): Promise<WhitelistRule[]> => {
      const res = await serviceApiV3.get<WhitelistRule[] | { data: WhitelistRule[] }>(
        serviceWhitelistPath(serviceId as string, siteId as string),
      );
      const body = res.data as WhitelistRule[] | { data?: WhitelistRule[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Save a site's Website-Whitelist rules, service-scoped. */
export function useUpdateServiceWebsiteWhitelist(
  serviceId: string,
  siteId: number | string,
  options?: UseMutationOptions<unknown, unknown, WhitelistRule[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rules: WhitelistRule[]) => {
      const res = await serviceApiV3.put(serviceWhitelistPath(serviceId, siteId), rules);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: webAccessServiceKeys.whitelist(serviceId, siteId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Remove an IP from the auto Web ACL, service-scoped. */
export function useRemoveServiceAutoBlackList(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  return useMutation({
    mutationFn: async (ip: string) => {
      const res = await serviceApiV3.post(
        `/services/${serviceId}/waap/auto-black-list`,
        { ip },
      );
      return res.data;
    },
    ...options,
  });
}
